import React, { useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  Stack,
  TextField
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  differenceInMinutes,
  format,
  getHours,
  isPast,
  isToday,
  isValid,
  isWeekend,
  parseISO
} from 'date-fns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptBRLocale from 'date-fns/locale/pt-BR';
import { toast } from 'react-toastify';
import Toast from '../../../../../../components/Toast';
import { options } from '../../../../../../helpers/toastOptions';
import { useAuth } from '../../../../../../hooks/Auth';
import { useIntegration } from '../../../../../../hooks/Integration';
import { useModal } from '../../../../../../hooks/Modal';
import { usePickup } from '../../../../../../hooks/Pickup';
import { halfHours as hours } from '../../../../../../helpers/hours';
import { SearchMyCollectionsContext } from '../..';
import * as S from './styled';
import { generateListFromInterval } from '../../../../../../helpers/hoursInterval';

export const ModalExtendPickup = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmExtension, setConfirmExtension] = useState(false);
  const [completedExtension, setCompletedExtension] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [inputAvailableDate, setInputAvailableDate] = useState(parseISO(''));
  const [extendReason, setExtendReason] = useState('');
  const [extensionOtherReason, setExtensionOtherReason] = useState('');
  const [pickupFromDate, setPickupFromDate] = useState('');
  const [pickupUntilDate, setPickupUntilDate] = useState('');
  const [fromHours, setFromHours] = useState([]);
  const [untilHours, setUntilHours] = useState([]);
  const [holidayList, setHolidayList] = useState([]);

  const { token } = useAuth();
  const { getHolidaysByZipPostalCode } = useIntegration();
  const { closeModal, Modal } = useModal();
  const { extendPickupNoInvoice } = usePickup();

  const {
    extensionReasonList,
    setExtensionReasonList,
    pickupIdToModal,
    zipToModal,
    protocolToModal,
    branchParameterizationToModal,
    setProtocolExtend,
    refreshSearch,
    openOtherExtensionToFill,
    enablePickupOnWeekends,
    hourLimitOnWeekends,
    enablePickupOnHolidays,
    hourLimitOnHolidays,
    enableSlotTimeToPickupSchedule,
    hourSlotTimeToPickupSchedule,
    minuteSlotTimeToPickupSchedule
  } = useContext(SearchMyCollectionsContext);

  const maxSM = useMediaQuery('(max-width:768px)');

  const extendPickupSchema = Yup.object().shape({
    loadAvailableDate: Yup.string()
      .required('Data obrigatória')
      .test(
        'loadAvailableDateTest',
        'A data de coleta deverá ser maior que a data de hoje!',
        (value) => !(isPast(new Date(value)) || isToday(new Date(value)))
      )
      .test(
        'loadAvailableDateWeekendTest',
        'A data informada para a coleta não está disponível!',
        (value) => {
          if (!enablePickupOnWeekends && isWeekend(new Date(value))) {
            return false;
          }

          if (
            !enablePickupOnHolidays &&
            holidayList?.includes(new Date(value).toDateString())
          ) {
            return false;
          }

          return true;
        }
      )
      .nullable(),
    loadAvailableFrom: Yup.string().required('Hora obrigatória').nullable(),
    loadAvailableUntil: Yup.string()
      .required('Hora obrigatória')
      .test(
        'loadAvailableUntilTest',
        'Hora final deve ser após hora inicial',
        (value, ctx) => {
          const fromDate = new Date(
            2000,
            0,
            1,
            ctx.parent.loadAvailableFrom.split(':')[0],
            ctx.parent.loadAvailableFrom.split(':')[1]
          );

          const untilDate = new Date(
            2000,
            0,
            1,
            value.split(':')[0],
            value.split(':')[1]
          );

          return !(differenceInMinutes(fromDate, untilDate) >= 0);
        }
      )
      .nullable(),
    extendReason: Yup.string().required('Motivo obrigatório'),
    extensionOtherReason: Yup.string()
  });

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    setError,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(extendPickupSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });

  const allFieldsFilled =
    !!inputAvailableDate &&
    !!watch('loadAvailableFrom') &&
    !!watch('loadAvailableUntil') &&
    !!extendReason;

  const onSubmit = async (data) => {
    if (
      !isValid(new Date(data.loadAvailableDate)) ||
      format(new Date(data.loadAvailableDate), 'yyyy')?.replace(/^0+/, '')
        ?.length < 4
    ) {
      setError('loadAvailableDate', {
        message: 'A data de coleta informada é inválida!'
      });
      return;
    }

    const date = new Date(data.loadAvailableDate)?.toISOString().split('T')[0];

    let [hour] = data.loadAvailableFrom.split(':');
    // const fromDate = `${date}T${String(hour).padStart(2, '0')}:00:00.000`;
    const fromDate = `${date}T${String(hour).padStart(2, '0')}:${
      data.loadAvailableFrom.split(':')[1]
    }:00.000`;

    [hour] = data.loadAvailableUntil.split(':');
    // const untilDate = `${date}T${String(hour).padStart(2, '0')}:00:00.000`;
    const untilDate = `${date}T${String(hour).padStart(2, '0')}:${
      data.loadAvailableUntil.split(':')[1]
    }:00.000`;
    setPickupFromDate(fromDate);
    setPickupUntilDate(untilDate);
    setConfirmExtension(true);
  };

  const handleExtendPickup = async () => {
    try {
      setIsSubmitting(true);

      const params = {
        managerParameterizationId: branchParameterizationToModal,
        pickupId: pickupIdToModal,
        extensionReasonId: extendReason,
        dateFrom: pickupFromDate,
        dateTo: pickupUntilDate,
        extensionOtherReason
      };

      const response = await extendPickupNoInvoice({ data: params, token });
      if (response.status === 200) {
        setProtocolExtend(protocolToModal);
        setConfirmExtension(false);
        setCompletedExtension(true);
        refreshSearch();
      } else {
        throw new Error();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);

      setConfirmExtension(false);
      setCompletedExtension(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const disableWeekends = (date) => {
    let isHoliday = false;

    if (holidayList?.length > 0) {
      const newDate = new Date(date).toDateString();

      isHoliday = holidayList?.includes(newDate);
    }

    return (
      (!enablePickupOnWeekends &&
        (date.getDay() === 0 || date.getDay() === 6)) ||
      (!enablePickupOnHolidays && isHoliday)
    );
  };

  useEffect(() => {
    if (isToday(inputAvailableDate)) {
      const hour = getHours(new Date());

      const hoursFiltered = hours.filter((value) => {
        return value.hour.split(':')[0] > hour;
      });

      setFromHours(hoursFiltered);

      const { loadAvailableFrom } = getValues();

      const findHour = hoursFiltered.findIndex((value) => {
        return value.hour === loadAvailableFrom;
      });

      if (findHour < 0) {
        setValue('loadAvailableFrom', '');
      }
    } else {
      setFromHours(hours);
    }
  }, [inputAvailableDate]);

  useEffect(() => {
    const {
      loadAvailableFrom: availableFrom,
      loadAvailableUntil: availableUntil
    } = getValues();

    let newHours = [...hours];

    if (availableFrom && enablePickupOnWeekends) {
      const loadAvailableFrom = String(availableFrom)?.split(':')[0];
      let maxHour = 24;

      if (isWeekend(inputAvailableDate)) {
        maxHour = hourLimitOnWeekends || 24;

        newHours = newHours.filter(({ hour }) => {
          return (
            hour.split(':')[0] > loadAvailableFrom &&
            hour.split(':')[0] <= maxHour
          );
        });
      }
    }

    if (availableFrom && enablePickupOnHolidays) {
      const loadAvailableFrom = String(availableFrom)?.split(':')[0];
      let maxHour = 24;

      if (holidayList?.includes(new Date(inputAvailableDate).toDateString())) {
        maxHour = hourLimitOnHolidays || 24;

        newHours = newHours.filter(({ hour }) => {
          return (
            hour.split(':')[0] > loadAvailableFrom &&
            hour.split(':')[0] <= maxHour
          );
        });
      }
    }

    // if (availableFrom && enableSlotTimeToPickupSchedule) {
    //   const loadAvailableFrom = String(availableFrom)?.split(':')[0];
    //   const interval = hourSlotTimeToPickupSchedule || 1;

    //   newHours = newHours.filter(({ hour }) => {
    //     return (
    //       hour.split(':')[0] > loadAvailableFrom &&
    //       (hour.split(':')[0] - loadAvailableFrom) % interval === 0
    //     );
    //   });
    // }
    if (availableFrom && enableSlotTimeToPickupSchedule) {
      const loadAvailableFrom = String(availableFrom)?.split(':')[0];
      const loadFromHour = String(availableFrom)?.split(':')[0];
      const fromMinute = String(availableFrom)?.split(':')[1];
      const interval = hourSlotTimeToPickupSchedule;

      const intervalMinute = minuteSlotTimeToPickupSchedule;
      const listHoursInterval = generateListFromInterval(
        `${loadFromHour}:${fromMinute}`,
        `${interval}:${intervalMinute}`,
        newHours
      );
      newHours = listHoursInterval;
    }

    const findHour = newHours.find(({ hour }) => {
      return hour === availableUntil;
    });

    if (!findHour) {
      setValue('loadAvailableUntil', '');
    }

    setUntilHours(newHours);
  }, [inputAvailableDate, watch('loadAvailableFrom')]);

  useEffect(async () => {
    if (zipToModal) {
      try {
        const holidaysResponse = await getHolidaysByZipPostalCode({
          zipPostalCode: zipToModal,
          token
        });

        if (holidaysResponse?.status === 200) {
          const holidays = holidaysResponse?.data?.data?.list.map((holiday) => {
            const date = new Date(holiday.date).toDateString();
            return date;
          });

          setHolidayList(holidays);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    }
  }, []);

  return (
    <Modal preventClose>
      <form onSubmit={handleSubmit(onSubmit)}>
        {!confirmExtension && !completedExtension && !confirmClose && (
          <Stack spacing={2} preventClose>
            <S.ModalTitle>Prorrogação da Coleta</S.ModalTitle>
            <S.SectionTitle>Selecione a nova data da coleta:</S.SectionTitle>
            <Stack direction={maxSM ? 'column' : 'row'} spacing={2}>
              <Controller
                name="loadAvailableDate"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    locale={ptBRLocale}
                  >
                    <DatePicker
                      label="Data"
                      mask="__/__/____"
                      {...field}
                      value={(field.value = inputAvailableDate)}
                      onChange={(value) => {
                        setInputAvailableDate(value);
                        field.onChange(value);
                      }}
                      shouldDisableDate={disableWeekends}
                      minDate={new Date()}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onBlur={(event) => {
                            if (
                              event?.target?.value?.length >= 8 &&
                              event?.target?.value?.length < 10
                            ) {
                              const [day, month, year] =
                                event.target.value.split('/');

                              const newDate = new Date(
                                `20${year?.replace(/^0+/, '')}`,
                                month - 1,
                                day
                              );

                              setInputAvailableDate(newDate);
                              setValue('loadAvailableDate', newDate);
                            }
                          }}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: 'DD/MM/AAAA'
                          }}
                          style={{ width: maxSM ? '100%' : 192 }}
                          error={!!errors.loadAvailableDate}
                          helperText={errors.loadAvailableDate?.message}
                        />
                      )}
                    />
                  </LocalizationProvider>
                )}
              />
              <Controller
                name="loadAvailableFrom"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <FormControl
                    error={!!errors.loadAvailableFrom}
                    style={{ width: maxSM ? '100%' : 128 }}
                  >
                    <InputLabel id="loadAvailableFrom">A partir de</InputLabel>
                    <Select
                      id="loadAvailableFrom"
                      label="A partir de"
                      {...field}
                      error={!!errors.loadAvailableFrom}
                    >
                      {fromHours.map(({ id, hour }) => (
                        <MenuItem key={id} value={hour}>
                          {hour}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors.loadAvailableFrom?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
              <Controller
                name="loadAvailableUntil"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <FormControl
                    error={!!errors.loadAvailableUntil}
                    style={{ width: maxSM ? '100%' : 128 }}
                  >
                    <InputLabel id="loadAvailableUntil">Até</InputLabel>
                    <Select
                      id="loadAvailableUntil"
                      label="Até"
                      {...field}
                      error={!!errors.loadAvailableUntil}
                    >
                      {untilHours.map(({ id, hour }) => (
                        <MenuItem key={id} value={hour}>
                          {hour}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors.loadAvailableUntil?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Stack>

            <Controller
              name="extendReason"
              defaultValue=""
              control={control}
              render={({ field }) => (
                <FormControl
                  error={!!errors.extendReason}
                  style={{ width: maxSM ? 360 : '100%' }}
                >
                  <InputLabel id="extendReason">
                    Selecione o motivo da prorrogação:
                  </InputLabel>
                  <Select
                    id="extendReason"
                    label="Selecione o motivo da prorrogação:"
                    {...field}
                    value={(field.value = extendReason)}
                    onChange={(event) => {
                      setExtendReason(event.target.value);
                      field.onChange(event.target.value);
                    }}
                    error={!!errors.extendReason}
                  >
                    {extensionReasonList.map(
                      ({ extensionDescription, extensionId }) => (
                        <MenuItem key={extensionId} value={extensionId}>
                          {extensionDescription}
                        </MenuItem>
                      )
                    )}
                  </Select>
                  <FormHelperText>
                    {errors.extendReason?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />

            {extensionReasonList?.find(
              (element) => element.extensionId === extendReason
            )?.extensionDescription === 'OUTROS MOTIVOS' &&
              openOtherExtensionToFill && (
                <Controller
                  name="otherExtendReason"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      name="otherExtendReason"
                      id="otherExtendReason"
                      label="Motivo"
                      {...field}
                      onChange={(e) => {
                        setExtensionOtherReason(e.target.value);
                        field.onChange(e.target.value);
                      }}
                      style={{ width: '100%' }}
                      error={!!errors.otherExtendReason}
                      helperText={errors.otherExtendReason?.message}
                    />
                  )}
                />
                // eslint-disable-next-line prettier/prettier
            )}

            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 32
              }}
            >
              <Button
                type="button"
                variant="contained"
                color="secondary"
                onClick={() => {
                  setConfirmClose(true);
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  extensionReasonList?.find(
                    (element) => element.extensionId === extendReason
                  )?.extensionDescription === 'OUTROS MOTIVOS' &&
                  openOtherExtensionToFill
                    ? !(allFieldsFilled && extensionOtherReason)
                    : !allFieldsFilled
                }
              >
                Confirmar
              </Button>
            </Box>
          </Stack>
        )}

        {confirmExtension && (
          <Stack spacing={2}>
            <S.ModalTitle>Prorrogar Coleta</S.ModalTitle>
            <S.ModalText>
              A coleta de Protocolo {protocolToModal} será prorrogada.
            </S.ModalText>
            <S.ModalText>Deseja Continuar?</S.ModalText>

            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 32
              }}
            >
              <Button
                type="button"
                variant="contained"
                color="attention"
                onClick={() => {
                  setExtensionReasonList([]);
                  closeModal();
                }}
                disabled={isSubmitting}
              >
                Não
              </Button>
              <LoadingButton
                type="button"
                variant="contained"
                onClick={() => {
                  handleExtendPickup();
                }}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Sim
              </LoadingButton>
            </Box>
          </Stack>
        )}

        {completedExtension && (
          <Stack spacing={2}>
            <S.ModalTitle>Prorrogação Concluída</S.ModalTitle>
            <S.ModalText>
              Sua solicitação para prorrogação da coleta de Protocolo{' '}
              {protocolToModal} foi realizada com sucesso.
              <br />
              <br />
              Em breve receberá um e-mail!
            </S.ModalText>

            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 32
              }}
            >
              <Button
                type="button"
                variant="contained"
                onClick={() => {
                  setExtensionReasonList([]);
                  closeModal();
                }}
              >
                Ok
              </Button>
            </Box>
          </Stack>
        )}

        {confirmClose && (
          <Stack spacing={2}>
            <S.ModalTitle>Atenção</S.ModalTitle>

            <S.ModalText>
              Deseja cancelar a solicitação de prorrogação?
            </S.ModalText>

            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 32
              }}
            >
              <Button
                type="button"
                variant="contained"
                color="attention"
                onClick={() => setConfirmClose(false)}
              >
                Não
              </Button>
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={() => {
                  setExtensionReasonList([]);
                  closeModal();
                }}
              >
                Sim
              </Button>
            </Box>
          </Stack>
        )}
      </form>
    </Modal>
  );
};
